import React, { useEffect, useState } from 'react';
import { stockService, MarketSentimentData } from '../../../services/stockService';
import GlassCard from '../../ui/GlassCard';

export const MarketSentimentCard: React.FC = () => {
  const [data, setData] = useState<MarketSentimentData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await stockService.getMarketSentiment();
        setData(result);
      } catch (error: any) {
        console.error('Failed to load sentiment:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <GlassCard className="p-4 mb-4 animate-pulse">
        <div className="h-20 bg-white/5 rounded"></div>
      </GlassCard>
    );
  }

  if (!data) return null;

  const { sentimentScore, sentimentDescription, marketBreadth, volumeAnalysis } = data;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-500';
    if (score >= 60) return 'text-orange-400';
    if (score >= 40) return 'text-yellow-400';
    if (score >= 20) return 'text-blue-400';
    return 'text-purple-400';
  };

  const scoreColor = getScoreColor(sentimentScore);
  const totalStocks = marketBreadth.upCount + marketBreadth.downCount + marketBreadth.flatCount;
  const upRatio = totalStocks > 0 ? (marketBreadth.upCount / totalStocks) * 100 : 0;
  const downRatio = totalStocks > 0 ? (marketBreadth.downCount / totalStocks) * 100 : 0;

  return (
    <GlassCard className="p-5 mb-6 border-white/10">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white">Market Sentiment</h3>
        <div className={`text-sm font-medium px-2 py-1 rounded bg-white/10 border border-white/5 ${scoreColor}`}>
          {sentimentDescription} ({Math.round(sentimentScore)})
        </div>
      </div>

      {/* Gauge / Progress Bar */}
      <div className="mt-8 mb-10 overflow-visible">
        <div className="relative h-2 bg-white/10 rounded-full overflow-visible">
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `100%`,
              background: `linear-gradient(90deg, #a855f7 0%, #3b82f6 25%, #eab308 50%, #f97316 75%, #ef4444 100%)`,
            }}
          />
          <div
            className="absolute top-1/2 w-4 h-4 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)] transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 z-10 border-2 border-gray-800"
            style={{ left: `${sentimentScore}%` }}
          />
        </div>
        <div className="flex justify-between mt-4 text-[10px] text-white/30 uppercase tracking-wider font-medium">
          <span>Extreme Fear</span>
          <span>Fear</span>
          <span>Neutral</span>
          <span>Greed</span>
          <span>Ext Greed</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-8">
        {/* Market Breadth */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/5">
          <div className="text-xs text-white/60 mb-3 font-medium uppercase tracking-wider">Market Breadth</div>

          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-red-400">Up</span>
                <span className="text-white/80">{marketBreadth.upCount}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: `${upRatio}%` }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-green-400">Down</span>
                <span className="text-white/80">{marketBreadth.downCount}</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: `${downRatio}%` }}></div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-white/5 flex justify-between text-[10px] text-white/40">
            <div className="flex flex-col items-center">
              <span className="mb-1">Limit Up</span>
              <span className="text-red-400 font-mono text-xs">{marketBreadth.limitUpCount}</span>
            </div>
            <div className="w-px bg-white/10 mx-2"></div>
            <div className="flex flex-col items-center">
              <span className="mb-1">Limit Down</span>
              <span className="text-green-400 font-mono text-xs">{marketBreadth.limitDownCount}</span>
            </div>
          </div>
        </div>

        {/* Volume & Technicals */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/5">
          <div className="text-xs text-white/60 mb-3 font-medium uppercase tracking-wider">Volume & Trend</div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-white/80">Vol Ratio (Up)</span>
              <span
                className={`text-xs font-mono font-medium ${volumeAnalysis.volumeRatio > 50 ? 'text-red-400' : 'text-green-400'}`}
              >
                {Math.round(volumeAnalysis.volumeRatio)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-white/80">Stocks &gt; MA20</span>
              <span className="text-xs font-mono font-medium text-yellow-400">
                {Math.round(data.technicalBreadth.aboveMa20Percent)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-white/80">Stocks &gt; MA60</span>
              <span className="text-xs font-mono font-medium text-yellow-400">
                {Math.round(data.technicalBreadth.aboveMa60Percent)}%
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-white/5">
              <span className="text-xs text-white/80">Breadth Score</span>
              <span className="text-xs font-mono font-medium text-blue-300">
                {Math.round(data.technicalBreadth.breadthScore)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
};
